import React, { useRef, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useToast } from '../contexts/ToastContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import UploadIcon from '@mui/icons-material/Upload';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { ClassSectionItem, SchoolsResponse, TeacherUser, UsersResponse } from '../types';

const normalizeTeacherIds = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((id) => {
      if (typeof id === 'object' && id !== null && '_id' in id) return String((id as { _id?: string })._id);
      return String(id);
    })
    .filter(Boolean);
};

const normalizeClassSection = (
  cs: ClassSectionItem & { classTeacherId?: unknown; sendFromClassTeacherIds?: unknown; isActive?: boolean }
): ClassSectionItem => {
  const rawId = cs.classTeacherId;
  const idStr =
    typeof rawId === 'object' && rawId !== null && '_id' in rawId
      ? String((rawId as { _id?: string })._id)
      : rawId
        ? String(rawId)
        : '';
  return {
    class: cs.class ?? '',
    section: cs.section ?? '',
    classTeacherId: idStr,
    classTeacherName: cs.classTeacherName ?? '',
    sendFromClassTeacherIds: normalizeTeacherIds(cs.sendFromClassTeacherIds),
    isActive: cs.isActive !== false,
  };
};

const emptyForm = (): ClassSectionItem => ({
  class: '',
  section: '',
  classTeacherId: '',
  classTeacherName: '',
  sendFromClassTeacherIds: [],
  isActive: true,
});

const Classes: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('classes');
  const [classSections, setClassSections] = useState<ClassSectionItem[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [form, setForm] = useState<ClassSectionItem>(emptyForm());
  const csvInputRef = useRef<HTMLInputElement>(null);

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const { data: teachersData } = useQuery<UsersResponse>(['users', 'TEACHER'], async () => {
    const res = await apiClient.get('/users?role=TEACHER');
    return res.data as UsersResponse;
  });
  const school = schoolsData?.schools?.[0];
  const teachers: TeacherUser[] = (teachersData?.users || []).filter((u) => u.role === 'TEACHER');

  React.useEffect(() => {
    if (school?.settings?.classSections?.length) {
      const raw = school.settings.classSections as (ClassSectionItem & { classTeacherId?: unknown; sendFromClassTeacherIds?: unknown })[];
      setClassSections(raw.map(normalizeClassSection));
    } else {
      setClassSections([]);
    }
  }, [school?.settings?.classSections]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const toPayload = (list: ClassSectionItem[]) =>
    list.map((cs) => ({
      class: cs.class,
      section: cs.section,
      classTeacherId: cs.classTeacherId?.trim() ? cs.classTeacherId : null,
      classTeacherName: (cs.classTeacherName || '').trim(),
      sendFromClassTeacherIds: (cs.sendFromClassTeacherIds || []).filter(Boolean),
      isActive: cs.isActive !== false,
    }));

  const handleSave = (newList: ClassSectionItem[]) => {
    if (!school) return;
    setClassSections(newList);
    saveMutation.mutate({
      _id: school._id,
      settings: {
        ...school.settings,
        classSections: toPayload(newList),
      },
    });
  };

  const handleAdd = () => {
    setForm(emptyForm());
    setAddOpen(true);
  };

  const handleAddSubmit = () => {
    if (!form.class.trim()) {
      showToast('error', 'Class is required.');
      return;
    }
    const newRow: ClassSectionItem = {
      ...form,
      class: form.class.trim(),
      section: (form.section || '').trim(),
      classTeacherName: form.classTeacherId ? teachers.find((t) => t._id === form.classTeacherId)?.name ?? '' : '',
    };
    handleSave([...classSections, newRow]);
    setAddOpen(false);
  };

  const handleEdit = (idx: number) => {
    setEditingIndex(idx);
    setForm({ ...classSections[idx] });
    setEditOpen(true);
  };

  const handleEditSubmit = () => {
    if (editingIndex < 0 || editingIndex >= classSections.length) return;
    if (!form.class.trim()) {
      showToast('error', 'Class is required.');
      return;
    }
    const newRow: ClassSectionItem = {
      ...form,
      class: form.class.trim(),
      section: (form.section || '').trim(),
      classTeacherName: form.classTeacherId ? teachers.find((t) => t._id === form.classTeacherId)?.name ?? '' : '',
      isActive: classSections[editingIndex].isActive,
    };
    const newList = classSections.map((cs, i) => (i === editingIndex ? newRow : cs));
    handleSave(newList);
    setEditOpen(false);
    setEditingIndex(-1);
  };

  const handleDelete = (idx: number) => {
    if (!window.confirm('Remove this class/section? This cannot be undone.')) return;
    const newList = classSections.filter((_, i) => i !== idx);
    handleSave(newList);
  };

  const handleToggleActive = (idx: number) => {
    const newList = classSections.map((cs, i) =>
      i === idx ? { ...cs, isActive: !cs.isActive } : cs
    );
    handleSave(newList);
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split(/\r?\n/).filter(Boolean);
      const rows: ClassSectionItem[] = [];
      let start = 0;
      if (lines.length > 0) {
        const first = lines[0].toLowerCase().replace(/\s/g, '');
        if (first === 'class,section' || first === 'class;section' || first.startsWith('class')) start = 1;
      }
      for (let i = start; i < lines.length; i++) {
        const parts = lines[i].split(/[,;\t]/).map((s) => s.trim());
        const cls = parts[0] ?? '';
        const sec = parts[1] ?? '';
        if (cls || sec)
          rows.push({
            class: cls,
            section: sec,
            classTeacherId: '',
            classTeacherName: '',
            sendFromClassTeacherIds: [],
            isActive: true,
          });
      }
      handleSave([...classSections, ...rows]);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const renderForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
      <TextField
        label="Class"
        value={form.class}
        onChange={(e) => setForm((f) => ({ ...f, class: e.target.value }))}
        placeholder="e.g. 1"
        required
      />
      <TextField
        label="Section"
        value={form.section}
        onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
        placeholder="e.g. A"
      />
      <FormControl fullWidth>
        <InputLabel>Class Teacher</InputLabel>
        <Select
          label="Class Teacher"
          value={form.classTeacherId ?? ''}
          onChange={(e) => {
            const id = e.target.value as string;
            const teacher = teachers.find((t) => t._id === id);
            setForm((f) => ({ ...f, classTeacherId: id, classTeacherName: teacher?.name ?? '' }));
          }}
        >
          <MenuItem value="">None</MenuItem>
          {teachers.map((t) => (
            <MenuItem key={t._id} value={t._id}>
              {t.name ?? t._id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Can send from class</InputLabel>
        <Select
          label="Can send from class"
          multiple
          value={form.sendFromClassTeacherIds ?? []}
          onChange={(e) => setForm((f) => ({ ...f, sendFromClassTeacherIds: e.target.value as string[] }))}
          renderValue={(selected) =>
            (selected as string[]).length
              ? (selected as string[])
                  .map((id) => teachers.find((t) => t._id === id)?.name ?? id)
                  .join(', ')
              : 'None'
          }
        >
          {teachers.map((t) => (
            <MenuItem key={t._id} value={t._id}>
              <Checkbox checked={(form.sendFromClassTeacherIds ?? []).indexOf(t._id) > -1} size="small" />
              <ListItemText primary={t.name ?? t._id} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  if (!authService.getStoredUser()) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1 }}>
        Classes &amp; sections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add class/section combinations. Set class teacher and who can send a student from class after a parent dispersal request. Disabled rows are hidden from dropdowns.
      </Typography>

      {canEdit && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add class/section
          </Button>
          <input
            type="file"
            ref={csvInputRef}
            accept=".csv,.txt"
            style={{ display: 'none' }}
            onChange={handleCsvUpload}
          />
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => csvInputRef.current?.click()}>
            Upload CSV
          </Button>
        </Box>
      )}

      {classSections.length === 0 ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No classes/sections yet. Add one or upload a CSV (class, section per row).
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Class Teacher</TableCell>
                <TableCell>Can send from class</TableCell>
                <TableCell align="center">Status</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {classSections.map((cs, idx) => (
                <TableRow key={idx}>
                  <TableCell>{cs.class}</TableCell>
                  <TableCell>{cs.section || '–'}</TableCell>
                  <TableCell>{cs.classTeacherName || '–'}</TableCell>
                  <TableCell>
                    {(cs.sendFromClassTeacherIds ?? []).length
                      ? (cs.sendFromClassTeacherIds ?? [])
                          .map((id) => teachers.find((t) => t._id === id)?.name ?? id)
                          .join(', ')
                      : '–'}
                  </TableCell>
                  <TableCell align="center">
                    {cs.isActive !== false ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Disabled" size="small" color="default" />
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" title="Edit" onClick={() => handleEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title={cs.isActive !== false ? 'Disable' : 'Enable'}
                        color={cs.isActive !== false ? 'warning' : 'success'}
                        onClick={() => handleToggleActive(idx)}
                        disabled={saveMutation.isLoading}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" onClick={() => handleDelete(idx)} disabled={saveMutation.isLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {classSections.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Total: {classSections.length} class/section(s)
        </Typography>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add class/section</DialogTitle>
        <DialogContent>{renderForm()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAddSubmit} disabled={saveMutation.isLoading}>
              Add
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingIndex(-1); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit class/section</DialogTitle>
        <DialogContent>{renderForm()}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditingIndex(-1); }}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleEditSubmit} disabled={saveMutation.isLoading}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Classes;
